/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { CreateCustomerOptions, FundingSource } from '@vsolv/dwolla/domain';
import { DwollaService } from '@vsolv/packages/reimbursement/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { CurrencyMaskConfig } from '@vsolv/vectors-ui/input';
import { Link } from '@wsphere/links/domain';
import { LinksWebService } from '@wsphere/links/web';
import { Claim } from '@wsphere/warranties/domain';
import { BehaviorSubject } from 'rxjs';
import { Payment } from '../../pages';
import { ClaimService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-payment-dialog',
  templateUrl: './payment.dialog.html',
  standalone: false,
})
export class PaymentDialog {
  readonly currencyMaskConfig: CurrencyMaskConfig = {
    align: 'left',
    allowNegative: false,
    allowZero: false,
    decimal: '.',
    nullable: false,
    precision: 2,
    prefix: '$',
    suffix: '',
    thousands: ',',
    inputMode: 1,
  };

  @ViewChild(DialogComponent) private readonly dialog?: DialogComponent;

  @Output() refresh = new EventEmitter<void>();
  @Output() refreshPaymentMethods = new EventEmitter<void>();

  protected lazyLoad$ = new BehaviorSubject<boolean>(true);

  @Input() canManagePaymentMethods: boolean | null = null;
  @Input() customer: CreateCustomerOptions | null = null;

  @Input() customerId: string | null = null;

  private formBuilder = inject(FormBuilder);
  private claimSvc = inject(ClaimService);
  private dwollaSvc = inject(DwollaService);
  private toastSvc = inject(ToastService);
  private linkSvc = inject(LinksWebService);

  protected readonly claim$ = this.claimSvc.getClaim();
  protected readonly payment$ = new BehaviorSubject<Claim.ClaimPayment.Model | null>(null);

  protected readonly destinations$ = new BehaviorSubject<FundingSource[] | null>(null);
  @Input() set destinations(destinations: FundingSource[] | null) {
    this.destinations$.next(destinations);
  }
  get destinations() {
    return this.destinations$.value;
  }

  private originalLinks: Link.FormLink[] | null = null;
  protected isEditing = false;

  /** The unpaid shares of the split to choose from; null when the claim doesn't pay from a split. */
  protected shares: Claim.Allocation.ListAllocationsItem[] | null = null;
  /** Why unpaid shares are left out, and what to do about it. */
  protected heldBackHint: string | null = null;

  /** The share of the split a new payment pays; it sets the payee, the amount and the authorization paid from. */
  protected share:
    | (Pick<Claim.ClaimPayment.Model, 'payeeType' | 'payeeName' | 'amount' | 'authorizationId'> & {
        allocationId: string;
      })
    | null = null;

  /** Authorization references by id, shown on each share once a claim has supplementals. */
  protected references: Record<string, string> | null = null;

  protected paymentForm = this.formBuilder.group({
    title: ['', [Validators.required, Validators.maxLength(255)]],
    description: ['', Validators.required],
    status: [null as Claim.ClaimPayment.Status | null],
    destination: [null as FundingSource | string | null],
    amount: [null as number | null, [Validators.required, Validators.min(0.01)]],
    links: [[] as Link.FormLink[]],
  });

  protected readonly paymentStatus = Claim.ClaimPayment.Status;
  protected readonly describePayee = Claim.Authorization.describePayee;

  /** Pass `retryOf` to start a new payment from a failed one; the failed payment stays as it was. */
  async open(claim: Claim.Model | null, payment?: Payment, retryOf?: Payment) {
    // Set on every open, since the dialog is reused. A retry pays the failed payment's share again.
    this.share = retryOf?.allocationId ? { ...retryOf, allocationId: retryOf.allocationId } : null;
    this.shares = null;
    this.heldBackHint = null;
    this.references = null;

    if (payment) await this.initializeForm(payment);
    else if (retryOf) await this.initializeRetry(retryOf, claim);
    else if (claim && claim.isAuthorizationRequired !== false) await this.initializeShares(claim);
    this.dialog?.open();
    this.loadForm();
  }

  protected selectShare(allocationId: string | null) {
    const allocation = this.shares?.find(share => share.id === allocationId);
    this.share = allocation ? { ...allocation, allocationId: allocation.id } : null;
    this.paymentForm.patchValue({ amount: allocation ? allocation.amount / 100 : null });
  }

  /** The balance of the authorization this payment is paid from: its own, the share's, else the latest. */
  protected getBalance(claim: Claim.Model | null, payment: Claim.ClaimPayment.Model | null) {
    const balances = claim?.paymentBalances ?? [];
    const authorizationId = payment?.authorizationId ?? this.share?.authorizationId;
    if (!authorizationId) return balances[balances.length - 1] ?? null;

    return balances.find(balance => balance.authorizationId === authorizationId) ?? null;
  }

  /** What this payment may use, in cents: the authorization's remaining amount, plus its own when editing a draft. */
  protected getRemaining(claim: Claim.Model | null, payment: Claim.ClaimPayment.Model | null) {
    const balance = this.getBalance(claim, payment);
    if (!balance) return null;

    const isOwnDraft =
      payment?.status === Claim.ClaimPayment.Status.DRAFT && payment.authorizationId === balance.authorizationId;
    return balance.remaining + (isOwnDraft ? payment.amount : 0);
  }

  protected isOverRemaining(claim: Claim.Model | null, payment: Claim.ClaimPayment.Model | null) {
    const remaining = this.getRemaining(claim, payment);
    const amount = this.paymentForm.value.amount;
    return remaining !== null && !!amount && Math.round(amount * 100) > remaining;
  }

  /** A share's amount comes from the split. */
  protected isAmountLocked(payment: Claim.ClaimPayment.Model | null) {
    return !!(payment ? payment.allocationId : this.share);
  }

  /** The edited payment's payee, the share's for a new one, else the active authorization's. Old claims are paid without one. */
  protected getPayee(claim: Claim.Model, payment: Claim.ClaimPayment.Model | null) {
    const authorization =
      claim.isAuthorizationRequired !== false ? Claim.Authorization.getActive(claim.authorizations ?? []) : null;
    // While a share is being chosen, the payee isn't known yet.
    const source = payment ?? this.share ?? (this.shares ? null : authorization);
    const label = source ? Claim.Authorization.describePayee(source) : null;
    if (!source || !label) return null;

    return { label, isShop: source.payeeType === Claim.Authorization.PayeeType.SERVICE_PROVIDER };
  }

  protected formatAmount(cents: number) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  }

  async save(claim: Claim.Model, editing: boolean, paymentId?: string) {
    if (this.paymentForm.value.title) {
      const customerId = claim.warranty?.customerId;

      const destination =
        typeof this.paymentForm.value.destination === 'string'
          ? this.paymentForm.value.destination
          : this.paymentForm.value.destination?.id;

      const payment: Claim.ClaimPayment.CreateClaimPaymentRequest | Claim.ClaimPayment.UpdateClaimPaymentRequest = {
        title: this.paymentForm.value.title,
        description: this.paymentForm.value.description ?? null,
        ...(editing && this.paymentForm.value.status && { status: this.paymentForm.value.status }),
        destination,
        amount: this.paymentForm.value.amount ?? 0,
        ...(customerId && !editing && { customerId }),
        // Lets the server refuse it if the claim was reversed while the dialog was open.
        ...(!editing && { authorizationId: this.getBalance(claim, null)?.authorizationId ?? null }),
        ...(!editing && this.share && { allocationId: this.share.allocationId }),
      };
      this.lazyLoad$.next(true);
      const response = !editing
        ? await this.createPayment(claim, payment as Claim.ClaimPayment.CreateClaimPaymentRequest)
        : paymentId
          ? await this.updatePayment(claim, paymentId, payment as Claim.ClaimPayment.UpdateClaimPaymentRequest)
          : null;

      const responseId = response && typeof response === 'object' && 'id' in response ? response.id : undefined;
      if (responseId) {
        if (response && typeof response === 'object' && 'claimId' in response) {
          await this.updateLinks(response as { id: string; claimId: string });
        }
        this.toastSvc.show({
          type: 'success',
          title: `Payment ${!editing ? 'created' : 'updated'}!`,
          text: `Your ${!editing ? 'new' : ''} payment "<strong>${payment.title}</strong>" has been successfully ${
            !editing ? 'created' : 'updated'
          }.`,
        });
        this.lazyLoad$.next(false);
        this.refresh.emit();
        this.close();
      }
    }
  }

  private async createPayment(claim: Claim.Model, payment: Claim.ClaimPayment.CreateClaimPaymentRequest) {
    const newPayment = await this.claimSvc.createPayment(claim.id, payment).catch(({ error }) => {
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: error.message,
      });
      this.lazyLoad$.next(false);
      this.close();
    });
    const newPaymentId = newPayment && typeof newPayment === 'object' && 'id' in newPayment ? newPayment.id : undefined;
    if (newPaymentId) {
      await this.linkSvc.create(
        { id: claim.id, objectType: Link.ObjectType.CLAIM },
        {
          object1: { id: claim.id, objectType: Link.ObjectType.CLAIM },
          object2: { id: newPaymentId, objectType: Link.ObjectType.PAYMENT },
        }
      );
    }
    return newPayment;
  }

  private async updatePayment(
    claim: Claim.Model,
    paymentId: string,
    payment: Claim.ClaimPayment.UpdateClaimPaymentRequest
  ) {
    return await this.claimSvc.updatePayment(claim.id, paymentId, payment).catch(({ error }) => {
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: error.message,
      });
      this.lazyLoad$.next(false);
      this.close();
    });
  }

  async updateLinks(payment: { id: string; claimId: string }) {
    const links = this.paymentForm.controls.links.value;

    // Add new links
    if (links?.length) {
      const originalLinkObjectIds = this.originalLinks?.map(link => (link.object as any)?.id);
      const originalLinkIds = this.originalLinks?.map(link => link.id);

      await Promise.all(
        links.map(async link => {
          if (!originalLinkIds?.includes(link.id) && !originalLinkObjectIds?.includes((link.object as any)?.id)) {
            await this.linkSvc.create(
              { id: payment.claimId, objectType: Link.ObjectType.CLAIM },
              {
                object1: { id: (link.object as any).id, objectType: link.type },
                object2: { id: payment.id, objectType: Link.ObjectType.PAYMENT },
              }
            );
          }
        })
      );
    }

    // Remove links
    if (this.originalLinks?.length) {
      const newLinks = links?.map(link => (link.object as any)?.id);

      await Promise.all(
        this.originalLinks?.map(async link => {
          if (link.id && !newLinks?.includes((link.object as any)?.id)) {
            await this.linkSvc.delete({ id: payment.claimId, objectType: Link.ObjectType.CLAIM }, link.id);
          }
        })
      );
    }
  }

  close() {
    this.dialog?.close();
  }

  protected refreshDestinations() {
    this.refreshPaymentMethods.emit();
  }

  private async getLinks(paymentId: string, claimId: string): Promise<Link.FormLink[] | null> {
    const links = (
      await this.linkSvc.list(
        { id: claimId, objectType: Link.ObjectType.CLAIM },
        { owner: { id: paymentId, objectType: Link.ObjectType.PAYMENT } }
      )
    ).items;
    let originalLinks: Link.FormLink[] = [];
    if (links.length) {
      originalLinks = Link.getPickerLinks(links, Link.ObjectType.PAYMENT, paymentId);
      if (originalLinks.length) {
        this.originalLinks = originalLinks;
      }
    }
    return originalLinks.length
      ? originalLinks.map(link => ({ id: link.id, object: link.object, type: link.type }))
      : null;
  }

  private async initializeForm(payment: Payment) {
    this.isEditing = true;
    this.paymentForm.reset();
    this.payment$.next(payment);
    const links = await this.getLinks(payment.id, payment.claimId);
    const destination = payment.destination
      ? await this.dwollaSvc.getFundingSource(payment.destination, payment.customerId)
      : null;
    this.paymentForm.patchValue({
      title: payment.title,
      description: payment.description,
      status: payment.status,
      destination: destination,
      amount: payment.amount > 0 ? payment.amount / 100 : null,
      links: links,
    });
    this.loadForm();
  }

  /** The unpaid shares of every authorization whose split nothing holds back; with only one left, it's chosen already. */
  private async initializeShares(claim: Claim.Model) {
    this.paymentForm.reset();

    const authorizations = Claim.Authorization.getActiveAuthorizations(claim.authorizations ?? []);
    const [{ items }, overrides] = await Promise.all([
      this.claimSvc.listAllocations(claim.id),
      this.claimSvc.listOverrides(claim.id),
    ]);
    // What an override that isn't approved holds back: the authorization, or one share.
    const blocking = overrides.items.filter(override => Claim.Override.BLOCKING_STATES.includes(override.state));
    const held = (id: string | undefined) => blocking.find(override => override.targetId === id);

    // A split is paid all or nothing: one share waiting on an approval holds back every share of it.
    const holds: Claim.Override.Model[] = [];
    this.shares = authorizations.flatMap(authorization => {
      const split = items.filter(
        allocation =>
          allocation.authorizationId === authorization.id && allocation.status === Claim.Allocation.Status.ACTIVE
      );
      const splitHolds = [authorization.id, ...split.map(share => share.id)]
        .map(held)
        .filter((hold): hold is Claim.Override.Model => !!hold);
      holds.push(...splitHolds);

      return splitHolds.length ? [] : split.filter(share => !share.paymentStatus);
    });
    this.heldBackHint = holds.map(hold => Claim.Override.heldBackMessage(hold)).join(' ') || null;
    if (authorizations.length > 1) {
      this.references = Object.fromEntries(authorizations.map(({ id, reference }) => [id, reference]));
    }
    if (this.shares.length === 1) this.selectShare(this.shares[0].id);
  }

  private async initializeRetry(failed: Payment, claim: Claim.Model | null) {
    this.paymentForm.reset();
    this.payment$.next(null);

    const links = await this.getLinks(failed.id, failed.claimId);
    // The links are added to the new payment, not moved from the failed one.
    this.originalLinks = null;

    const destination = failed.destination
      ? await this.dwollaSvc.getFundingSource(failed.destination, failed.customerId)
      : null;
    const remaining = this.getRemaining(claim, null) ?? failed.amount;

    this.paymentForm.patchValue({
      title: failed.title,
      description: failed.description,
      destination,
      amount: Math.min(failed.amount, remaining) / 100 || null,
      links,
    });
  }

  private loadForm() {
    this.lazyLoad$.next(false);
    setTimeout(() => {
      this.lazyLoad$.next(true);
    }, 500);
  }
}
